/**
 * Auto-create children (FLOW-747)
 *
 * A button on the work item detail page's Child Items header that asks the
 * AI whether the item is complex enough to warrant breaking down, and a
 * panel below the header that shows the result: either the AI's reason for
 * declining (with a "Propose anyway" escape hatch) or a prunable checklist
 * of proposed children the user can create in one go.
 *
 * Both components share a single `UseChildBreakdownResult` so the button
 * (which lives in the header row) and the panel (which lives below it) stay
 * in sync without either owning the state itself.
 */

import { Component, For, Show } from "solid-js";
import { Button } from "../ui/Button";
import type { UseChildBreakdownResult } from "../../hooks/useChildBreakdown";

export interface AutoCreateChildrenProps {
  breakdown: UseChildBreakdownResult;
}

export const AutoCreateChildrenButton: Component<AutoCreateChildrenProps> = (
  props,
) => {
  return (
    <Button
      variant="ghost"
      size="sm"
      loading={props.breakdown.busy()}
      disabled={props.breakdown.busy() || !!props.breakdown.proposal()}
      onClick={() => props.breakdown.propose(false)}
    >
      Auto-create children
    </Button>
  );
};

export const ProposedChildrenPanel: Component<AutoCreateChildrenProps> = (
  props,
) => {
  return (
    <Show when={props.breakdown.proposal() || props.breakdown.error()}>
      <div class="mb-3 rounded border border-line bg-paper p-3 text-sm">
        <Show when={props.breakdown.proposal()}>
          {(proposal) => (
            <Show
              when={proposal().warrants_breakdown}
              fallback={
                <div>
                  <p class="text-ink-2">{proposal().reason}</p>
                  <div class="mt-2 flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={props.breakdown.busy()}
                      disabled={props.breakdown.busy()}
                      onClick={() => props.breakdown.propose(true)}
                    >
                      Propose anyway
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => props.breakdown.dismiss()}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              }
            >
              <p class="text-ink-2 mb-2">{proposal().reason}</p>
              <div class="space-y-1 mb-3">
                <For each={proposal().children}>
                  {(child, index) => (
                    <label class="flex items-start gap-2 p-2 rounded border border-line bg-surface hover:border-line-strong cursor-pointer">
                      <input
                        type="checkbox"
                        aria-label={child.title}
                        checked={props.breakdown.selected().has(index())}
                        class="mt-0.5 w-4 h-4 accent-ink rounded"
                        onChange={() => props.breakdown.toggle(index())}
                      />
                      <div class="flex-1 min-w-0">
                        <div class="font-medium text-ink">{child.title}</div>
                        <Show when={child.description}>
                          <div class="text-xs text-ink-3">
                            {child.description}
                          </div>
                        </Show>
                      </div>
                    </label>
                  )}
                </For>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={props.breakdown.selectedCount() === 0}
                  loading={props.breakdown.creating()}
                  onClick={() => props.breakdown.create()}
                >
                  Create {props.breakdown.selectedCount()} children
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => props.breakdown.dismiss()}
                >
                  Cancel
                </Button>
              </div>
            </Show>
          )}
        </Show>
        <Show when={props.breakdown.error()}>
          <p role="alert" class="mt-2 text-signal-red">
            {props.breakdown.error()}
          </p>
        </Show>
      </div>
    </Show>
  );
};

export default ProposedChildrenPanel;
